/**
 * Google Maps API Service via Supabase Edge Function
 * Resolve problema de CORS chamando via servidor
 * Suporta BYOK: passe store_id para buscar chave do banco
 */

import { supabase } from './supabase';

export interface GoogleMapsDistanceResult {
  distanceKm: number;
  durationMins: number;
  durationNoTrafficMins?: number;
  routeSummary?: string | null;
  source?: 'google_directions_min_time' | 'google_distance_matrix' | string;
  status: string;
}

export interface GoogleMapsDistanceError {
  error: string;
  status?: string;
  elementStatus?: string;
}

/**
 * Calcula distância e tempo usando a Edge Function do Supabase
 * Evita CORS porque é chamada do servidor (Edge Function)
 * Suporta BYOK: passe storeId no config para buscar chave automaticamente
 */
export const getGoogleMapsDistanceViaEdge = async (
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number,
  googleMapsApiKeyOrConfig: string | { storeId?: string; googleMapsApiKey?: string }
): Promise<GoogleMapsDistanceResult | null> => {
  // Handle both old signature (apiKey string) and new (config object with storeId)
  let googleMapsApiKey: string | undefined;
  let storeId: string | undefined;
  
  if (typeof googleMapsApiKeyOrConfig === 'string') {
    googleMapsApiKey = googleMapsApiKeyOrConfig;
  } else if (googleMapsApiKeyOrConfig) {
    storeId = googleMapsApiKeyOrConfig.storeId;
    googleMapsApiKey = googleMapsApiKeyOrConfig.googleMapsApiKey;
  }
  
  try {
    const { data, error } = await supabase.functions.invoke('google-maps-proxy', {
      body: {
        store_id: storeId,
        apiKey: googleMapsApiKey,
        originLat,
        originLng,
        destLat,
        destLng
      }
    });

    if (error) {
      console.error('[GoogleMapsEdge] Edge Function Error:', error);
      throw new Error(`Edge Function Error: ${error.message || error}`);
    }

    if (data && typeof data.distanceKm === 'number' && typeof data.durationMins === 'number') {
      return {
        distanceKm: data.distanceKm,
        durationMins: data.durationMins,
        durationNoTrafficMins: data.durationNoTrafficMins,
        routeSummary: data.routeSummary,
        source: data.source,
        status: data.status || 'OK'
      };
    }

    console.warn('[GoogleMapsEdge] Resposta inválida:', data);
    return null;
    
  } catch (e: any) {
    console.error('[GoogleMapsEdge] Exception:', e.message);
    return null;
  }
};

/**
 * Wrapper para usar tanto via Edge Function (browser) quanto direto (server)
 * Detecta automaticamente o ambiente
 */
export const getGoogleMapsDistance = async (
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number,
  googleMapsApiKeyOrConfig: string | { storeId?: string; googleMapsApiKey?: string },
  useEdgeFunction: boolean = true
): Promise<GoogleMapsDistanceResult | null> => {
  // Se estamos no navegador, usar Edge Function (evita CORS)
  if (useEdgeFunction && typeof window !== 'undefined') {
    return getGoogleMapsDistanceViaEdge(originLat, originLng, destLat, destLng, googleMapsApiKeyOrConfig);
  }
  
  // Se estamos no servidor (Edge Function), chamar direto
  // Nota: Este código só executa se chamado de outra Edge Function ou server-side
  let apiKey = typeof googleMapsApiKeyOrConfig === 'string' ? googleMapsApiKeyOrConfig : googleMapsApiKeyOrConfig?.googleMapsApiKey;
  if (!apiKey) return null;
  
  const url = `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${originLat},${originLng}&destinations=${destLat},${destLng}&mode=driving&key=${apiKey}`;
  
  try {
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.status === 'OK' && data.rows?.[0]?.elements?.[0]?.status === 'OK') {
      const el = data.rows[0].elements[0];
      return {
        distanceKm: el.distance.value / 1000,
        durationMins: Math.ceil(el.duration.value / 60),
        status: 'OK'
      };
    }
    return null;
  } catch (e) {
    console.error('[GoogleMaps] Direct call error:', e);
    return null;
  }
};
